import React, { useEffect, useState } from "react";
import {
  Button,
  Form,
  FormItem,
  InputGroup,
  Input,
} from "@clarkinc/zest-design-system/ui";
import { FieldErrors, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { validatePhoneNumber } from "@wss/utilities/form";
import handleTextMessageSubmission from "~/common/ProductPromotion/handleTextMessageSubmission";

import { User, withUserAndChat } from "~/common/Store/connectUserAndChat";

interface Props extends React.HTMLAttributes<HTMLDivElement> {
  itemNumber: string;
  user: User;
}

interface Result {
  success: boolean;
  message: string;
}

const formSchema = z.object({
  phoneNumber: z
    .string()
    .nonempty("Phone number is required.")
    .transform(validatePhoneNumber("Please enter a valid phone number.")),
});

const GetPriceByTextForm = ({ className, itemNumber, user }: Props) => {
  const [result, setResult] = useState<Result>();
  const [disabled, setDisabled] = useState(true);

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: { phoneNumber: user.billingPhoneNumber ?? "" },
  });

  useEffect(() => {
    setDisabled(user.index === undefined);
  }, [user.index]);

  useEffect(() => {
    form.setValue(
      "phoneNumber",

      user.billingPhoneNumber as string & { __tag: "E164Number" },
    );
  }, [user.billingPhoneNumber, form]);

  async function onError(errors: FieldErrors<z.infer<typeof formSchema>>) {
    if (errors.phoneNumber?.message) {
      setResult({
        success: false,
        message: errors.phoneNumber.message,
      });
    }
  }

  const handleSubmit = async ({
    phoneNumber,
  }: z.infer<typeof formSchema>): Promise<void> => {
    const response = await handleTextMessageSubmission({
      phoneNumber,
      itemNumber,
    });

    if (response.isError) {
      setResult({
        success: false,
        message:
          "There was an error sending the text message. Please try again.",
      });
      return;
    }
    setResult({
      success: true,
      message:
        "A message has been sent to the contact information you provided.",
    });
  };

  return (
    <div className="mt-1-1/4">
      {(!result || !result.success) && (
        <div className={`flex ${className}`}>
          <Form
            form={form}
            onSubmit={form.handleSubmit(handleSubmit, onError)}
            aria-label="Get Price By Text"
            theme="wss"
            className="w-5/6 min-w-35 max-w-44"
          >
            <InputGroup
              theme="wss"
              groupLabel="Get Price By Text Input Field"
              inputSize="small"
            >
              <FormItem name="phoneNumber">
                <Input
                  theme="wss"
                  type="tel"
                  label=""
                  hideLabel
                  hideError
                  data-testid="phone-form-input"
                  disabled={disabled}
                  format
                />
              </FormItem>
              <Button
                theme="wss"
                type="submit"
                color="gray"
                className="whitespace-nowrap"
                disabled={disabled}
              >
                Text Price
              </Button>
            </InputGroup>
          </Form>
        </div>
      )}
      {result && <Message success={result.success}>{result.message}</Message>}
    </div>
  );
};

function Message({
  success,
  children,
}: {
  success: boolean;
  children: React.ReactNode;
}) {
  return success ? (
    <div role="alert" className="text-xs leading-none whitespace-normal">
      {children}
    </div>
  ) : (
    <div className="text-xs leading-none mt-1-1/4 text-red-500 whitespace-normal">
      {children}
    </div>
  );
}

export default withUserAndChat(GetPriceByTextForm);
